import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../../lib/load-failure';
import { distinctContactCandidates, type SavedContact } from '../../lib/contact-candidates';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';

/**
 * The client's saved contacts, offered as one-tap recipients in the
 * Send-to-Customer modal (brief 7-F3).
 *
 * ONE READ, the customer's whole list, then scoped here. Three questions are
 * asked of it and they need different slices: the picker wants this rig's rows
 * (or every row when the rig has none), and the "already a contact" test wants
 * the slice the API's save would match against — this rig's rows when the
 * address came from the rig field, the customer-wide (no-rig) rows when it came
 * from the company field. A `rig_name=` read cannot answer the second case,
 * because it never returns the no-rig rows. The rig filter here is the API's
 * own rule (`LOWER(TRIM(rig_name)) = LOWER(trimmed param)`), not a new one.
 */

export interface CustomerContactRow extends SavedContact {
  rigName?: string | null;
  rig_name?: string | null;
}

const norm = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();
const rigOf = (c: CustomerContactRow) => c.rigName ?? c.rig_name ?? null;

/** This rig's rows, by the API's matching rule. A blank rig matches the rows with no rig. */
export function contactsForRig(rows: CustomerContactRow[], rig: string | null | undefined): CustomerContactRow[] {
  const want = norm(rig);
  return rows.filter((c) => norm(rigOf(c)) === want);
}

/**
 * Is `address` already a saved contact in the slice the API's save would match?
 * Mirrors api#852's rule: same customer, same rig (trimmed, case-insensitive,
 * no-rig = no-rig), same `lower(btrim(email))`. The rig applies only when the
 * address came from the rig field AND the job has a rig; otherwise the save is
 * customer-wide and so is the match.
 */
export function isSavedContact(
  rows: CustomerContactRow[],
  address: string,
  opts: { fromRigField: boolean; rigName: string | null | undefined },
): boolean {
  const target = norm(address);
  if (!target) return false;
  const scope = opts.fromRigField && norm(opts.rigName) ? opts.rigName : null;
  return contactsForRig(rows, scope).some((c) => norm(c.email) === target);
}

/** The read. Disabled until the modal is open and the ticket names a customer. */
export function useSendModalContacts(customerId: string | null | undefined, enabled: boolean) {
  return useQuery<CustomerContactRow[]>({
    // Under the 'customer-contacts' prefix so every contact write already
    // invalidates it, but with its own tail: CustomerDetail caches a
    // differently-shaped answer under ['customer-contacts', id].
    queryKey: ['customer-contacts', customerId, 'send-modal'],
    queryFn: () => api.customers.listContacts(customerId!).then((r) => {
      const d = r.data;
      return Array.isArray(d) ? d : (d?.data ?? d?.contacts ?? []);
    }),
    enabled: enabled && !!customerId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export interface SendModalSavedContactsProps {
  rows: CustomerContactRow[] | undefined;
  isLoading: boolean;
  error: unknown;
  rigName: string;
  /** The address currently in the send field, to mark the picked row. */
  currentAddress: string;
  onPick: (contact: SavedContact) => void;
}

export function SendModalSavedContacts({ rows, isLoading, error, rigName, currentAddress, onPick }: SendModalSavedContactsProps) {
  // Failed or refused: say so where the list would be. Typing below is untouched.
  if (error) return <LoadFailureNotice error={error} subject="saved contacts" className="rounded-fi-control border border-fieldiq-border px-3 py-3" />;
  // Loading: no list, and no placeholder rows that read as "none".
  if (isLoading || !rows) return null;

  const onRig = rigName.trim() ? contactsForRig(rows, rigName) : [];
  const useRig = onRig.length > 0;
  const shown = distinctContactCandidates(useRig ? onRig : rows);
  const heading = useRig ? `Saved contacts · ${rigName.trim()}` : 'All rigs';

  if (shown.length === 0) {
    return <p data-testid="send-saved-contacts-empty" className="text-xs text-fieldiq-muted">No saved contacts for this client.</p>;
  }

  return (
    <div data-testid="send-saved-contacts">
      <p className="text-xs text-fieldiq-muted mb-1 font-medium">{heading}</p>
      <ul className="max-h-40 overflow-y-auto rounded-fi-control border border-fieldiq-border divide-y divide-fieldiq-border">
        {shown.map((c, i) => {
          const email = (c.email ?? '').trim();
          const picked = !!email && norm(email) === norm(currentAddress);
          return (
            <li key={c.id ?? (email || `no-email-${i}`)}>
              <button
                type="button"
                disabled={!email}
                aria-pressed={email ? picked : undefined}
                onClick={() => onPick(c)}
                className={`w-full px-3 py-2 text-left text-sm outline-none focus-visible:bg-fi-amber/10 disabled:cursor-not-allowed disabled:opacity-60 ${
                  picked ? 'bg-fi-amber/10' : 'hover:bg-fieldiq-dark/50'
                }`}
              >
                <span className="block font-medium text-fieldiq-text">{c.name?.trim() || '—'}</span>
                <span className="block text-xs text-fieldiq-muted break-all">
                  {email || 'No email on file'} · {c.phone?.trim() || '—'}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
